import { lazy, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useWaitlist } from "@/contexts/WaitlistContext";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { CITIES, cityPath } from "@/seo/shared";

// El formulario de contacto para salas (radix-dialog + react-hook-form + zod)
// va en su propio chunk y solo se carga al pasar por encima o pulsar.
const ContactDialog = lazy(() => import("@/components/ContactDialog"));
const prefetchDialog = () => void import("@/components/ContactDialog");

export function Footer() {
  const { openPreferences } = useCookieConsent();
  const { t } = useTranslation();

  return (
    // Exploración: pie en banda morada. Enlaces a la izquierda y el logo grande
    // a la derecha, abajo (en móvil, debajo de los enlaces).
    <footer className="bg-rc-violet text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <FooterWaitlist />
        {/* Orden en móvil: enlaces, logo, letra pequeña. En escritorio el logo
            ocupa la columna derecha de arriba abajo. */}
        <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_auto] md:gap-x-12 md:gap-y-10">
          <nav className="data-voice flex flex-col items-start gap-1 text-sm font-bold">
            {/* Las carteleras de ciudad en todas las páginas (31-10-2026): Google
                tenía /valencia «descubierta, sin indexar» por falta de enlaces. */}
            {CITIES.map((c) => (
              <FootLink key={c.slug} to={cityPath(c.slug)}>
                {t("seo.city.link", { city: c.name })}
              </FootLink>
            ))}
            <FootLink to="/peliculas">{t("footer.peliculas")}</FootLink>
            <FootLink to="/cines">{t("footer.cines")}</FootLink>
            <FootLink to="/suscripcion">{t("nav.subscription")}</FootLink>
            <PartnerLink />
          </nav>

          <div className="flex flex-col gap-3 md:row-span-2 md:items-end md:self-end">
            <img
              src="https://asrgzcggnwnsvpcsmxxw.supabase.co/storage/v1/object/public/media/brand/RECINE_white.svg?v=2"
              alt="RE:CINE"
              width={264}
              height={80}
              className="h-16 w-auto self-start sm:h-20 md:h-28 md:self-end lg:h-32"
            />
            <p className="data-voice text-xs text-white md:text-right">{t("footer.tagline")}</p>
          </div>

          <div>
            <nav className="data-voice flex flex-wrap gap-x-5 gap-y-1 text-[11px]">
              <FootLink to="/aviso-legal">{t("footer.avisoLegal")}</FootLink>
              <FootLink to="/privacidad">{t("footer.privacidad")}</FootLink>
              <FootLink to="/cookies">{t("footer.cookies")}</FootLink>
              <FootLink to="/terminos">{t("footer.terminos")}</FootLink>
              <button
                type="button"
                onClick={openPreferences}
                className="data-voice text-white transition-colors duration-200 hover:text-rc-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime focus-visible:ring-offset-2 focus-visible:ring-offset-rc-violet rounded px-1 py-1 active:scale-95"
              >
                {t("footer.cookiePrefs")}
              </button>
            </nav>
            <p className="data-voice mt-3 px-1 text-[10px] text-white">
              {t("footer.rights", { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

/** Lista de espera en el pie de todas las páginas (29-10-2026, como la
 *  newsletter de Cineville). El correo abre el diálogo de siempre ya
 *  relleno, para confirmar la ciudad y ver el aviso de privacidad. */
function FooterWaitlist() {
  const { t } = useTranslation();
  const { openWaitlist } = useWaitlist();
  const [email, setEmail] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        openWaitlist("footer", email.trim() || undefined);
      }}
      className="mb-10 flex flex-col gap-5 border-b-2 border-white/60 pb-10 md:flex-row md:items-end md:justify-between"
    >
      <div>
        <p className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-[-0.03em] text-rc-lime">{t("footer.waitlistTitle")}</p>
        <p className="data-voice mt-3 text-xs text-white">{t("footer.waitlistBody")}</p>
      </div>
      <div className="flex w-full max-w-md gap-2">
        <label htmlFor="footer-waitlist-email" className="sr-only">
          {t("waitlist.emailLabel")}
        </label>
        <input
          id="footer-waitlist-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("waitlist.emailPlaceholder")}
          className="min-w-0 flex-1 rounded-full border-2 border-rc-navy bg-white px-4 py-2.5 text-sm text-rc-navy placeholder:text-rc-navy/45 focus:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime"
        />
        <button
          type="submit"
          className="data-voice shrink-0 rounded-full border-2 border-rc-navy bg-rc-lime px-5 py-2.5 text-xs text-rc-navy transition-[transform,box-shadow] duration-200 ease-spring hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-hard-sm active:translate-x-0 active:translate-y-0 active:shadow-none active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-rc-violet"
        >
          {t("waitlist.submit")}
        </button>
      </div>
    </form>
  );
}

/** «¿Tienes una sala? Escríbenos»: antes era un bloque entero al final de la
 *  portada; las salas no nos llegan por la web, así que basta un enlace
 *  (29-10-2026, usuario). */
function PartnerLink() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  return (
    <>
      <button
        type="button"
        onMouseEnter={prefetchDialog}
        onFocus={prefetchDialog}
        onClick={() => {
          setMounted(true);
          setOpen(true);
        }}
        className="data-voice text-white transition-colors duration-200 hover:text-rc-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime focus-visible:ring-offset-2 focus-visible:ring-offset-rc-violet rounded px-1 py-1 active:scale-95"
      >
        {t("footer.partner")}
      </button>
      {mounted && (
        <Suspense fallback={null}>
          <ContactDialog open={open} onOpenChange={setOpen} />
        </Suspense>
      )}
    </>
  );
}

function FootLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="data-voice text-white transition-colors duration-200 hover:text-rc-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime focus-visible:ring-offset-2 focus-visible:ring-offset-rc-violet rounded px-1 py-1 active:scale-95"
    >
      {children}
    </Link>
  );
}
